import { useNavigate, useSearchParams } from 'react-router-dom';
import EntityDialog from '../../EntityDialog';
import { USER_ROLE_OPTIONS } from '../../constants';
import {
  addUser,
  getUserAccessBrands,
  getUserAccessFacilities,
  getUserAccessMarkets,
} from '../../hooks/useUsers';
import isValidEmail from '../../utils/valEmail';
import {
  toUserAccessScopePayload,
  validateUserAccessScope,
} from '../../utils/permissions';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { getErrorMessage } from '../../utils/requestErrors';
import UserAccessScopeFields from '../../UserAccessScopeFields';
import useRequestedOrganizationSelection from '../../hooks/useRequestedOrganizationSelection';

const userAccessApi = {
  getUserAccessMarkets,
  getUserAccessFacilities,
  getUserAccessBrands,
};

export default function AddUserContainer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { org } = useLegacyAppSession();
  const requestedOrganizationName = String(searchParams.get('organization') || '');
  const {
    isResolvingOrganization,
    organizationResolutionError,
  } = useRequestedOrganizationSelection(requestedOrganizationName);

  return (
    <EntityDialog
      title="Invite User"
      contextLabel="Users"
      testId="invite-user"
      fields={[
        { name: 'name', label: 'Name', required: true },
        { name: 'email', label: 'Email', required: true },
        {
          name: 'role',
          label: 'Role',
          type: 'select',
          options: USER_ROLE_OPTIONS,
          required: true,
        },
        { name: 'isApprovingUserRequestToJoinOrganization', isHidden: true },
        {
          name: 'accessScope',
          type: 'org-selector',
          excludeFromPreview: true,
          render: ({ form, setValue }) => (
            <UserAccessScopeFields
              api={userAccessApi}
              organizationId={org?.id}
              form={form}
              setValue={setValue}
            />
          ),
        },
      ]}
      initialValues={() => ({
        name: String(searchParams.get('name') || ''),
        email: String(searchParams.get('email') || ''),
        role: 'Viewer',
        isApprovingUserRequestToJoinOrganization:
          searchParams.get('isApprovingUserRequestToJoinOrganization')?.trim() === 'true',
        allMarkets: false,
        marketIds: [],
        defaultMarketId: null,
        allFacilities: false,
        facilityIds: [],
        allBrands: false,
        brandIds: [],
      })}
      onClose={() => navigate('/users', { replace: true })}
      saveDisabled={!org?.id || isResolvingOrganization || Boolean(organizationResolutionError)}
      topMessage={
        organizationResolutionError ||
        (isResolvingOrganization ? 'Selecting organization...' : null) ||
        (!org?.id ? 'Please select an organization before continuing.' : null)
      }
      onSave={async (payload) => {
        try {
          if (!isValidEmail(payload?.email)) {
            return showToastError('The email address appears invalid.');
          }
          if (!org?.id) {
            return showToastError('An organization must be selected.');
          }
          const scopeError = validateUserAccessScope(payload);
          if (scopeError) {
            return showToastError(scopeError);
          }

          await addUser({
            email: payload.email,
            name: payload.name,
            organizationId: org.id,
            roleName: payload.role,
            isApprovingUserRequestToJoinOrganization:
              Boolean(payload.isApprovingUserRequestToJoinOrganization),
            ...toUserAccessScopePayload(payload),
          });

          showToastSuccess(
            `${payload.name} was ${
              payload.isApprovingUserRequestToJoinOrganization ? 'approved' : 'invited'
            } successfully.`,
          );
          navigate('/users', { replace: true });
        } catch (error) {
          console.error(error);
          showToastError(getErrorMessage(error, 'An error occurred creating the user.'));
        }
      }}
    />
  );
}
